// Styles extracted so the timed-out branch and the main branch share the
// exact same look — keeps the recovery state on-brand instead of falling
// through to default browser styling. Also exported for reuse in GmailSyncOverlay.
export function SetupScreenStyles() {
  return (
    <style>{`
      .setup-screen {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--color-bg, #F8F9FA);
        color: var(--color-text, #1a1a1a);
        z-index: 9999;
      }
      .setup-content {
        /* Explicit width (not just max-width) so the container does NOT
           reflow when the suggestion bubble's text changes during the
           carousel. Otherwise center-aligned children visibly shift
           left/right on every prompt rotation. */
        text-align: center;
        width: min(480px, calc(100vw - 32px));
        padding: 40px;
        box-sizing: border-box;
      }
      /* Logo block — pulse-glow idiom shared with XaviorPulseLoader,
         Gmail-sync welcome, and the page-build loader. See #2408. */
      .setup-logo {
        position: relative;
        width: 56px;
        height: 56px;
        margin: 0 auto 32px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .setup-logo-glow {
        position: absolute;
        inset: -22px;
        border-radius: 9999px;
        filter: blur(20px);
        background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 45%, #6366f1 100%);
        animation: setup-logo-glow-breath 2.2s cubic-bezier(0.45, 0, 0.55, 1) infinite;
        will-change: transform, opacity;
        transform: translateZ(0);
        pointer-events: none;
      }
      @keyframes setup-logo-glow-breath {
        0%, 100% { opacity: 0.28; transform: scale(0.98); }
        45%, 55% { opacity: 0.42; transform: scale(1.05); }
      }
      .setup-logo-img {
        position: relative;
        width: 56px;
        height: 56px;
      }
      .setup-content h2 {
        font-size: 20px;
        font-weight: 600;
        color: var(--color-text-heading, #111111);
        margin: 0 0 28px;
        letter-spacing: -0.01em;
      }
      .setup-steps {
        /* width: fit-content + auto margins centers the rows as a single
           block (icon column aligned, labels left-aligned within the
           block). Without this the rows stretch to the parent's fixed
           480px and cling to the left edge. See #2408. */
        display: flex;
        flex-direction: column;
        width: fit-content;
        margin: 0 auto 28px;
        gap: 14px;
        text-align: left;
      }
      .setup-step {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 14px;
        color: var(--color-text-muted, #303336);
        transition: color 0.3s ease, transform 0.3s ease;
        opacity: 0.65;
      }
      .setup-step.done {
        color: var(--color-text-subtle, #2d2f31);
        opacity: 1;
      }
      .setup-step.done .step-icon {
        color: #16a34a; /* success green — kept literal; theme has no semantic success token */
      }
      .step-icon {
        width: 20px;
        text-align: center;
        font-size: 14px;
        color: var(--color-text-faint, #505459);
        transition: color 0.3s ease;
      }
      .setup-step:not(.done) .step-icon {
        animation: step-icon-pulse 1.3s ease-in-out infinite;
      }
      @keyframes step-icon-pulse {
        0%, 100% { opacity: 0.35; }
        50% { opacity: 0.85; }
      }
      .step-label {
        font-feature-settings: 'tnum';
      }
      .setup-divider {
        height: 1px;
        background: var(--color-border-muted, #E8E9EC);
        margin: 8px 0 24px;
      }
      .suggestions-label {
        font-size: 12px;
        color: var(--color-text-faint, #505459);
        text-transform: uppercase;
        letter-spacing: 0.06em;
        margin: 0 0 12px;
        font-weight: 500;
      }
      .suggestion-bubble {
        font-size: 15px;
        color: var(--color-text-subtle, #2d2f31);
        font-style: italic;
        min-height: 56px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: opacity 0.3s ease, transform 0.3s ease;
        line-height: 1.5;
      }
      .fade-in { opacity: 1; transform: translateY(0); }
      .fade-out { opacity: 0; transform: translateY(4px); }
      .setup-content p {
        color: var(--color-text-muted, #303336);
        margin-bottom: 16px;
      }
      .setup-slow-note {
        margin-top: 18px;
        padding: 12px 14px;
        border: 1px solid var(--color-border, rgba(0,0,0,0.08));
        border-radius: 12px;
        color: var(--color-text-muted, #6b7280);
        font-size: 13px;
        text-align: center;
      }
      .setup-slow-note p {
        margin: 0 0 8px;
        color: inherit;
      }
      .setup-actions {
        display: inline-flex;
        flex-direction: column;
        gap: 10px;
        align-items: center;
      }
      .setup-retry-btn {
        background: var(--color-primary, #3C4043);
        color: #fff;
        border: none;
        padding: 10px 28px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.15s ease;
      }
      .setup-retry-btn:hover {
        background: var(--color-primary-hover, #202124);
      }
      /* Secondary action — text-only so it doesn't compete with Refresh. */
      .setup-report-btn {
        background: transparent;
        color: var(--color-text-muted, #303336);
        border: none;
        padding: 6px 12px;
        font-size: 13px;
        font-weight: 400;
        cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 3px;
        text-decoration-color: var(--color-border-strong, #BDC1C6);
        transition: color 0.15s ease, text-decoration-color 0.15s ease;
      }
      .setup-report-btn:hover {
        color: var(--color-text, #1a1a1a);
        text-decoration-color: var(--color-text, #1a1a1a);
      }
      .setup-tip-carousel {
        margin-top: 28px;
        padding-top: 20px;
        border-top: 1px solid var(--color-border-muted, #E8E9EC);
        text-align: center;
      }
      .setup-tip-label {
        display: block;
        font-size: 10px;
        font-weight: 500;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--color-text-faint, #505459);
        margin-bottom: 8px;
      }
      .setup-tip-text {
        font-size: 13px;
        color: var(--color-text-subtle, #6b7280);
        line-height: 1.5;
        margin: 0;
        height: 40px;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: tip-fade-in 0.4s ease;
      }
      @keyframes tip-fade-in {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      @media (prefers-reduced-motion: reduce) {
        .setup-logo-glow { animation: none !important; opacity: 0.32; }
        .setup-step:not(.done) .step-icon { animation: none !important; opacity: 0.6; }
        .setup-tip-text { animation: none !important; }
        .setup-step, .suggestion-bubble, .setup-retry-btn { transition: none !important; }
      }
    `}</style>
  );
}
